import React from 'react';
import moment from 'moment';
import { SegmentProps } from 'tea-component';
import TipContext from '@home/components/TipContext';
import { getSpecialListValues } from '@gp/utils/common';
import { BoolFlag } from '@gp/enums/common';
import { GROUP_KEY } from '@gp/constants/select';
import { ICONS, GR_DOC_URL, PARAMS_MAP, SCHEDULE_FILTER_MILESTONES_CFG } from './constants';

import type { IDateValue } from '@gp/components/ComposedDatePicker/types';
import type { IScheduleFilterMetadata } from '../hooks/useOptionsAndValues';

export const formatDateForFilter = (
  dateType: ScheduledateTypes,
  date: string,
  action: 'startOf' | 'endOf',
  formatter = 'YYYY-MM-DD',
): string => {
  if (!dateType || !date) return '';
  const dateM = moment(date, 'YYYY-MM-DD');
  if (!dateM.isValid) return '';
  return dateM[action](dateType).format(formatter);
};

export const formatDateForServer = (date?: string): string => date?.replaceAll('-', '') || '';

export const getCommonCalendarList = (data: ScheduleCalendarDataTypes): CommonCalendarDataTypes => {
  let res: CommonCalendarDataTypes = null;
  if (!data || !Array.isArray(data)) return res;
  res = [];
  for (let i = 0, l = data?.length; i < l; i++) {
    const t = data?.[i];
    const list: CommonCalendarCardDataTypes = Array.isArray(t?.list)
      ? t?.list?.map((it) => ({
          ...it,
          uniqueId: `${t?.start}_${it?.id}`,
          isTBC: it?.start_date_confirm === '2',
          projectName: it?.project,
          projectId: it?.project_id,
          type: it?.is_gr === BoolFlag.True ? 1 : 0,
          isExpire: it?.is_expire,
          isJump: true,
        }))
      : null;
    res.push({
      ...t,
      date: t?.start,
      list,
    });
  }

  return res;
};

export const filterFn = (milestone: CommonCalendarCardItemTypes, legendSelected: boolean[]) => {
  const { type, isExpire, milestone_history: milestoneHistories } = milestone || {};
  let flag = false;
  const isOfficialGateReview = type === 1;
  const isOverdue = !!isExpire;

  if (!flag && legendSelected?.[0]) {
    flag = isOfficialGateReview;
  }
  if (!flag && legendSelected?.[1]) {
    flag = !isOfficialGateReview && !isOverdue;
  }
  if (!flag && legendSelected?.[2]) {
    flag = isOverdue;
  }
  // 开启Time Change过滤，仅展示有Time Change数据的日历卡片
  if (legendSelected?.[3]) {
    flag = !!milestoneHistories?.length;
  }

  return flag;
};

export const getDefaultDates = (ranges?: {
  month: IDateValue;
  quarter: IDateValue;
  year: IDateValue;
}): {
  month: IDateValue;
  quarter: IDateValue;
  year: IDateValue;
} => {
  const { month: monthRange, quarter: quarterRange, year: yearRange } = ranges || {};
  const [monthRangeStartDate = '', monthRangeEndDate = ''] = monthRange || [];
  const [quarterRangeStartDate = '', quarterRangeEndDate = ''] = quarterRange || [];
  const [yearRangeStartDate = '', yearRangeEndDate = ''] = yearRange || [];
  const startOfMonth = moment().startOf('month');
  const startOfQuarter = moment().startOf('quarter');
  const startOfYear = moment().startOf('year');

  const monthStartDate = startOfMonth.isAfter(moment(monthRangeStartDate))
    ? startOfMonth.format('YYYY-MM-DD')
    : monthRangeStartDate;

  const quarterStartDate = startOfQuarter.isAfter(moment(quarterRangeStartDate))
    ? startOfQuarter.format('YYYY-MM-DD')
    : quarterRangeStartDate;

  const yearStartDate = startOfYear.isAfter(moment(yearRangeStartDate))
    ? startOfYear.format('YYYY-MM-DD')
    : yearRangeStartDate;

  return {
    month: [monthStartDate, monthRangeEndDate],
    quarter: [quarterStartDate, quarterRangeEndDate],
    year: [yearStartDate, yearRangeEndDate],
  };
};

export const getCalendarDataFetchParams = (
  segment: ScheduleSegmentTypes,
  filter: IPipelineOverviewScheduleFilterVO,
  options: IScheduleFilterMetadata<IGPSelectOptionWithGroup>,
  milestoneOptions: IGPSelectOptionWithGroup[],
): IPipelineOverviewScheduleFetchParams => {
  const {
    composedDatePicker,
    pteam,
    business_owner: businessOwner,
    studio,
    project,
    platform,
    milestone,
    publisher,
    status,
    stage,
    form,
    project_id,
  } = filter || {};
  const { granularity, date } = composedDatePicker || {};
  const [startDate, endDate] = date || [];

  return {
    // 1: Official Review, 2: All Milestone, 3: Launch Schedule
    type: 2, // 固定为all
    // type: Number(segment) as 1 | 2 | 3,
    // 1: Month, 2: Quarter, 3: Year
    granularity: PARAMS_MAP[granularity]?.granularity,
    // 20230421
    start_date: formatDateForServer(startDate),
    // 20231231
    end_date: formatDateForServer(endDate),
    pteam: getSpecialListValues(pteam, options?.pteam) || [],
    business_owner: getSpecialListValues(businessOwner, options?.business_owner) || [],
    // 放id
    studio: getSpecialListValues(studio, options?.studio) || [],
    // 放id
    project: getSpecialListValues(project, options?.project) || [],
    platform: getSpecialListValues(platform, options?.platform) || [],
    milestone: [],
    // milestone: getSpecialListValues(milestone, milestoneOptions) || [],
    publisher: getSpecialListValues(publisher, options?.publisher) || [],
    status: getSpecialListValues(status, options?.status) || [],
    stage: getSpecialListValues(stage, options?.stage) || [],
    form: getSpecialListValues(form, options?.form) || [],
    project_id: project_id || [],
  };
};

export const updateIcons = (payload?: Record<string, Record<string, any>>) => {
  if (!payload) return ICONS;
  return ICONS.map((iconItem) => {
    const { key } = iconItem;
    const iconPayload = payload?.[key] || {};
    return { ...iconItem, ...iconPayload };
  });
};

export const getSegmentOptions = (
  segment?: ScheduleSegmentTypes,
  loading?: boolean,
): SegmentProps['options'] => [
  {
    text: (
      <div className="dbg-schedule-category-segment__segment-gr">
        <span>Official Gate Reviews</span>
        <TipContext
          overlayClassName="dbg-bubble-max-width"
          selected={segment === '1'}
          tipText={
            <div>
              <p>
                GRs are official reviews of the projects by the committee to determine if they
                should continue and approve budget to the next GR. GRs typically include the
                Concept, End of Pre-Production (Vertical Slice), Feature Complete (Alpha), and
                Content Complete (Beta) milestones.
              </p>
              <p>Other milestones are internal checkpoints between pteam and studio.</p>
              <a target="_blank" href={GR_DOC_URL} rel="noreferrer">
                Learn more
              </a>
            </div>
          }
        />
      </div>
    ),
    value: '1',
    disabled: loading,
  },
  { text: 'All Milestones', value: '2', disabled: loading },

  { text: 'Launch Schedule', value: '3', disabled: loading },
];

export const filterCalendarHasTimeChange = (list?: any): any => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.filter((item) => {
    const { milestone_history: milestoneHistories } = item || {};
    return Array.isArray(milestoneHistories) && milestoneHistories.length > 0;
  });
};

export const getMilestoneOptionsForms = (milestoneList: any) => {
  return milestoneList?.map((item: string) => {
    return { value: item, text: item };
  });
};

export const getMilestoneOptions = (segment: ScheduleSegmentTypes, milestoneDataList: any = {}) => {
  const milestoneOptionsTmp = milestoneDataList[segment];
  if (!Array.isArray(milestoneOptionsTmp) || !milestoneOptionsTmp.length) return [];
  return milestoneOptionsTmp.map((milestoneOptionTmp) => {
    return { ...milestoneOptionTmp, groupKey: GROUP_KEY, label: milestoneOptionTmp?.text || '' };
  });
};
